import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.jsx'
import { useUserData } from '../lib/useUserData.js'
import { prettyDate } from '../lib/dates.js'
import MiniChart from '../components/MiniChart.jsx'
import Seo from '../components/Seo.jsx'
import { WELLNESS_CALCS } from '../data/wellness.js'

export { WELLNESS_CALCS }

export default function Wellness() {
  const { user } = useAuth()
  const { wellness, deleteWellnessLog } = useUserData()

  // group saved logs by metric type, ascending by date
  const groups = {}
  for (const l of wellness) (groups[l.type] ||= []).push(l)

  return (
    <main className="page">
      <Seo
        title="Health & Wellness Calculators | CalculateMyPeptides"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Wellness', path: '/wellness' }]}
        description="Free health calculators: BMI, BMR, TDEE / daily calories, body-fat %, and calories burned. Save results and track your progress over time."
      />
      <div className="container">
        <div style={{ marginBottom: 8 }}>
          <span className="pill blue">Free tools</span>
        </div>
        <h1 className="section-title">Health &amp; Wellness Calculators</h1>
        <p className="section-sub" style={{ maxWidth: '54ch' }}>
          Quick, no-signup calculators for the everyday numbers — bodyweight, metabolism,
          and energy. Built to pair with your peptide and dosing tools.
        </p>
        <p className="soft" style={{ maxWidth: '68ch' }}>
          Use the <strong>BMI</strong> and <strong>Body Fat %</strong> tools to gauge body composition, then
          <strong> BMR</strong> and <strong>TDEE</strong> to learn how many calories you burn at rest and across
          a full day — the foundation of any cut, bulk, or maintenance plan. The <strong>Calories Burned</strong>
          calculator rounds it out for individual workouts. Create a free account to save any result and chart your
          progress over time. Everything here is an educational estimate, not medical advice.
        </p>

        {user && Object.keys(groups).length > 0 && (
          <section style={{ marginBottom: 34 }}>
            <h2 className="section-title" style={{ fontSize: '1.35rem' }}>Your progress</h2>
            <div className="wl-grid">
              {Object.entries(groups).map(([type, logs]) => {
                const label = logs[0].label || type
                const unit = logs[logs.length - 1].unit || ''
                const first = logs[0].value
                const last = logs[logs.length - 1].value
                const change = Math.round((last - first) * 10) / 10
                return (
                  <div className="card card-pad wl-card" key={type}>
                    <div className="between">
                      <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{label}</h3>
                      <span className="muted small">{logs.length} entr{logs.length === 1 ? 'y' : 'ies'}</span>
                    </div>
                    <MiniChart points={logs.map((l) => ({ date: l.date, value: l.value }))} unit={unit} />
                    <div className="between wl-card-foot">
                      <span className="small">
                        Latest <strong>{last}{unit}</strong>
                        {logs.length > 1 && (
                          <span className={change === 0 ? 'muted' : change < 0 ? 'wl-down' : 'wl-up'}>
                            {' '}({change > 0 ? '+' : ''}{change}{unit} since {prettyDate(logs[0].date)})
                          </span>
                        )}
                      </span>
                      <button className="btn btn-ghost btn-sm" onClick={() => deleteWellnessLog(logs[logs.length - 1].id)}>Remove last</button>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        <div className="pep-grid">
          {WELLNESS_CALCS.map((c) => {
            const card = (
              <>
                <div className="pep-card-top">
                  <div className="wc-icon">{c.icon}</div>
                  <h3>{c.name}</h3>
                  <p className="pep-summary">{c.desc}</p>
                </div>
                <div className="pep-tags">
                  {c.ready
                    ? <span className="pep-arrow">Open →</span>
                    : <span className="pill amber">Coming soon</span>}
                </div>
              </>
            )
            return c.ready ? (
              <Link key={c.slug} to={`/wellness/${c.slug}`} className="card pep-card fade-in">{card}</Link>
            ) : (
              <div key={c.slug} className="card pep-card is-soon">{card}</div>
            )
          })}
        </div>
      </div>
    </main>
  )
}
